import { DeepLink, DeepLinkResolution } from "./DeepLink";
import { DeepLinkResolver } from "./DeepLinkResolver";
import { DeepLinkStore } from "./DeepLinkStore";
import { API_BASE_URL } from "../constants/api";
import { INVALID_PAGE } from "../constants/storage";
import { URLS } from "../constants/urls";

/**
 * Public API facade that composes DeepLinkResolver and DeepLinkStore.
 *
 * Exports a singleton instance for use across the application.
 */
export const deepLinkService = {
  /**
   * Purely determines what the URL means. No side effects.
   *
   * @param url The URL pathname to resolve
   * @returns The resolution result (VALID with DeepLink or INVALID with reason)
   */
  resolve(url: string): DeepLinkResolution {
    return DeepLinkResolver.resolve(url);
  },

  /**
   * Persists a validated deeplink (writes localStorage + REQUEST_NUMBER cookie).
   *
   * @param deepLink The deeplink to persist
   */
  save(deepLink: DeepLink): void {
    DeepLinkStore.save(deepLink);
  },

  /**
   * Reads the current pending deeplink without clearing it.
   *
   * @returns The pending DeepLink, or null
   */
  get(): DeepLink | null {
    return DeepLinkStore.get();
  },

  /**
   * Returns the current pending deeplink and clears sessionStorage.
   * Does NOT clear the REQUEST_NUMBER cookie.
   *
   * @returns The pending DeepLink, or null
   */
  consume(): DeepLink | null {
    return DeepLinkStore.consume();
  },

  /**
   * Clears all pending deeplink state (sessionStorage + REQUEST_NUMBER cookie).
   */
  clear(): void {
    DeepLinkStore.clear();
  },

  /**
   * Handles post-login redirect logic for both SA and email login flows.
   *
   * 1. If user was on an error page (INVALID_PAGE flag set), redirect to home.
   * 2. If requireSuccessParam is true (SA login), only proceed when ?success=1
   *    is present in the URL (page reloaded from external SA portal redirect).
   * 3. Consume the pending deep link from localStorage and redirect to its
   *    full destination.
   *
   * @param requireSuccessParam If true (default), only redirects when ?success=1
   *   is present. Used by SA login. If false, redirects unconditionally.
   *   Used by email login (direct call after OTP verification, no page reload).
   * @returns true if a redirect was performed, false otherwise.
   */
  consumeAndRedirect(requireSuccessParam = true): boolean {
    // Escape error pages — redirect to home instead of reloading the error
    if (sessionStorage.getItem(INVALID_PAGE)) {
      sessionStorage.removeItem(INVALID_PAGE);
      window.location.replace(URLS.ROOT);
      return true;
    }

    if (requireSuccessParam) {
      const params = new URLSearchParams(window.location.search);
      if (!params.get("success")) return false;
    }

    const deepLink = this.consume();
    if (!deepLink) return false;

    window.location.replace(`${API_BASE_URL}${deepLink.destination}`);
    return true;
  },
};

export type DeepLinkService = typeof deepLinkService;
